import React, { useState, useMemo, useRef, useEffect } from "react";
import Icon from "../components/Icon";
import { useData } from "../data/DataProvider";
import { personAssignments, isBenchEligible } from "../services/utilization";
import { askResourceBot } from "../services/resourceAi";
import "../styles.css";

// ── Lightweight markdown renderer (no external dep) ──
function inlineBold(text) {
  return String(text).split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
}
const splitRow = (r) => r.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map(s => s.trim());

function Markdown({ text }) {
  const lines = String(text || "").replace(/\r/g, "").split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^\s*$/.test(line)) { i++; continue; }
    if (/^\s*---+\s*$/.test(line)) { blocks.push({ t: "hr" }); i++; continue; }
    // table
    if (line.includes("|") && i + 1 < lines.length && /^\s*\|?[\s:|-]*-[\s:|-]*$/.test(lines[i + 1])) {
      const header = splitRow(line); i += 2; const rows = [];
      while (i < lines.length && lines[i].includes("|")) { rows.push(splitRow(lines[i])); i++; }
      blocks.push({ t: "table", header, rows }); continue;
    }
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) { blocks.push({ t: "h", level: h[1].length, text: h[2] }); i++; continue; }
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*]\s+/, "")); i++; }
      blocks.push({ t: "ul", items }); continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+\.\s+/, "")); i++; }
      blocks.push({ t: "ol", items }); continue;
    }
    const para = [line]; i++;
    while (i < lines.length && !/^\s*$/.test(lines[i]) && !/^\s*[-*]\s+/.test(lines[i]) && !/^(#{1,4})\s+/.test(lines[i]) && !lines[i].includes("|") && !/^\s*\d+\.\s+/.test(lines[i])) { para.push(lines[i]); i++; }
    blocks.push({ t: "p", text: para.join(" ") });
  }
  return (
    <div>
      {blocks.map((b, k) => {
        if (b.t === "hr") return <hr key={k} style={{ border: "none", borderTop: "1px solid var(--border)", margin: "10px 0" }} />;
        if (b.t === "h") return <div key={k} style={{ fontWeight: 800, fontSize: b.level <= 2 ? 14 : 12.5, margin: "10px 0 4px", color: "var(--text-primary)" }}>{inlineBold(b.text)}</div>;
        if (b.t === "ul") return <ul key={k} style={{ margin: "4px 0 8px", paddingLeft: 18 }}>{b.items.map((it, j) => <li key={j} style={{ marginBottom: 3 }}>{inlineBold(it)}</li>)}</ul>;
        if (b.t === "ol") return <ol key={k} style={{ margin: "4px 0 8px", paddingLeft: 18 }}>{b.items.map((it, j) => <li key={j} style={{ marginBottom: 3 }}>{inlineBold(it)}</li>)}</ol>;
        if (b.t === "table") return (
          <table key={k} style={{ borderCollapse: "collapse", margin: "6px 0", fontSize: 12, width: "100%" }}>
            <thead><tr>{b.header.map((c, j) => <th key={j} style={{ textAlign: "left", padding: "4px 8px", borderBottom: "2px solid var(--border)", fontWeight: 800 }}>{inlineBold(c)}</th>)}</tr></thead>
            <tbody>{b.rows.map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci} style={{ padding: "4px 8px", borderBottom: "1px solid var(--border)" }}>{inlineBold(c)}</td>)}</tr>)}</tbody>
          </table>
        );
        return <p key={k} style={{ margin: "0 0 8px", lineHeight: 1.55 }}>{inlineBold(b.text)}</p>;
      })}
    </div>
  );
}

export default function ResourceAI() {
  const { people, teamMembers, projects, loading, error, getToken } = useData();
  const [messages, setMessages] = useState([]); // {role, text}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  // Available bench = active, bench-eligible people with open capacity TODAY.
  // Uses current (time-aware) load, so back-to-back assignments don't hide
  // someone behind a phantom 200% lifetime sum.
  const bench = useMemo(() => {
    return (people || [])
      .filter((p) => p.IsActive !== false && isBenchEligible(p))
      .map((p) => {
        const u = personAssignments(p, teamMembers || [], projects || []);
        return { ...p, capacity: Math.max(0, 100 - u.currentUtil) };
      })
      .filter((p) => p.capacity > 0)
      .sort((a, b) => b.capacity - a.capacity);
  }, [people, teamMembers, projects]);

  const openNeeds = useMemo(() => (projects || [])
    .filter((p) => p.Status === "Potential")
    .map((p) => ({ jobName: p.JobName || p.Title, buildingType: p.BuildingType, confidence: p.Confidence != null ? Number(p.Confidence) : null })),
    [projects]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, busy]);

  const send = async (q) => {
    const question = (q ?? input).trim();
    if (!question || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: question }]);
    setBusy(true);
    try {
      const answer = await askResourceBot({ question, bench, openNeeds, token: await getToken() });
      setMessages((m) => [...m, { role: "assistant", text: answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "⚠ " + (e.message || "Something went wrong. The AI endpoint may be unavailable.") }]);
    } finally { setBusy(false); }
  };

  if (loading) return <div className="page"><div className="empty"><div className="spinner" /><div className="empty-text" style={{ marginTop: 16 }}>Loading…</div></div></div>;
  if (error) return <div className="page"><div className="empty"><div className="empty-text">Couldn't load data: {error}</div></div></div>;

  const quickPicks = [
    ...openNeeds.slice(0, 4).map((n) => ({ label: `Who fits ${n.jobName}?`, q: `Who from the bench should we staff on ${n.jobName}${n.buildingType ? ` (a ${n.buildingType} job)` : ""}, and in what roles?` })),
    { label: "Best available PM", q: "Who is the best available project manager on the bench right now, and why?" },
    { label: "Best available Superintendent", q: "Who is the best available superintendent on the bench right now, and why?" },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Resource AI <span className="nav-beta" style={{ verticalAlign: "middle" }}>BETA</span></div>
          <div className="page-subtitle">Ask who from the bench to staff — reasoned over Culture Index profiles & availability</div>
        </div>
      </div>

      <div className="rai-grid">
        {/* Chat */}
        <div className="card rai-chat">
          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, paddingRight: 4 }}>
            {messages.length === 0 && (
              <div className="empty" style={{ margin: "auto", maxWidth: 420 }}>
                <div className="empty-icon"><Icon name="brain" /></div>
                <div className="empty-text">Ask a staffing question, or use a quick pick.<br />e.g. "Who should run a fast-paced multifamily job as superintendent?"</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: m.role === "user" ? "85%" : "92%", background: m.role === "user" ? "var(--navy-mid)" : "var(--bg-elevated)", color: m.role === "user" ? "#fff" : "var(--text-primary)", padding: "10px 14px", borderRadius: 12, fontSize: 13, lineHeight: 1.5 }}>
                {m.role === "assistant" ? <Markdown text={m.text} /> : <span style={{ whiteSpace: "pre-wrap" }}>{m.text}</span>}
              </div>
            ))}
            {busy && <div style={{ alignSelf: "flex-start", color: "var(--text-muted)", fontSize: 13, padding: "6px 4px" }}><span className="spinner" style={{ width: 14, height: 14, display: "inline-block", verticalAlign: "middle", marginRight: 8 }} /> Thinking…</div>}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <input className="form-input" style={{ flex: 1 }} placeholder="Ask about staffing the bench…" value={input}
              onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} disabled={busy} />
            <button className="btn btn-primary" onClick={() => send()} disabled={busy || !input.trim()}>Ask</button>
          </div>
        </div>

        {/* Sidebar: quick picks + bench summary */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="card">
            <div className="card-header"><div className="card-title">Quick picks</div></div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {quickPicks.map((qp, i) => (
                <button key={i} className="btn btn-ghost" style={{ fontSize: 12, textAlign: "left", justifyContent: "flex-start" }} onClick={() => send(qp.q)} disabled={busy}>{qp.label}</button>
              ))}
            </div>
          </div>
          <div className="card">
            <div className="card-header"><div className="card-title">Available bench</div><div style={{ fontSize: 12, color: "var(--text-muted)" }}>{bench.length}</div></div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, maxHeight: 260, overflowY: "auto" }}>
              {bench.length === 0 ? <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No one with open capacity.</div> :
                bench.map((p) => (
                  <div key={p.Id} style={{ fontSize: 12, display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.FullName} <span style={{ color: "var(--text-muted)" }}>· {p.CompanyRole}</span></span>
                    <span style={{ color: "var(--green)", fontWeight: 700 }}>{p.capacity}%</span>
                  </div>
                ))}
            </div>
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>
            Beta — suggestions are AI-generated from Culture Index profiles and current availability. Always apply your own judgment.
          </div>
        </div>
      </div>
    </div>
  );
}
